/**
 * InitializeCommunityVideoUseCase
 *
 * Community counterpart of {@link InitializeChatVideoUseCase}: initializes
 * encrypted community-video playback for a community message `messageId`.
 *
 * Responsibilities (mirrors the chat / user-gen flow exactly):
 * 1. Call the `generateCommunityMediaCdnTokens(messageId, communityOwner)`
 *    callable (Task 9) to get the Media CDN HMAC token + the encrypted
 *    decryption/iv keys. This callable authorizes the caller server-side against
 *    the community access rule (the wall owner always has access; every other
 *    viewer needs an active exclusive-content subscription to the owner); a null
 *    result is the no-subscription / permission-denied gate surfacing (the page
 *    shows the error / InfoSheet).
 * 2. Run the SAME `videoEncryptionService.processVideoEncryption(messageId, uid,
 *    iat, cdnTokens)` the chat / user-gen path uses (the community `messageId`
 *    is the encryption id) to decrypt + unshift the keys into a
 *    `VideoDecryptionEntity` with loader-compatible `VideoDecryptionKeys`. The
 *    COMMUNITY secret-name prefixes MUST be passed so the client unshifts with
 *    the same `expause-community-*` names the server shifted with.
 * 3. Build the community Media CDN video URL via `MediaUtils.getCommunityVideoUrl`
 *    → `${CDN_NAME}community/{owner}/{messageId}/master.m3u8?hdnts=<token>`.
 *    Unlike chat there is NO user-pair to sort: `owner` is the single
 *    community-wall owner, so the URL agrees byte-for-byte with the transcode
 *    output root (Task 2 `communityVideoOutputBasePath`), the message's
 *    `mediaInfo.path` (Task 5), the `generateCommunityMediaCdnTokens` CF's
 *    `PathGlobs` authorization scope (Task 7), and `getCommunityVideoUrl`
 *    (Task 10).
 * 4. Return `{ success, decryptionEntity, videoUrl, error }` — the SAME result
 *    interface as {@link InitializeChatVideoResult} so the player wiring can
 *    feed `createEncryptedLoader(messageId, decryptionEntity.keys)` to hls.js
 *    unchanged.
 *
 * Web-first design — no Flutter parity (community media encryption is Web-only).
 */

import type { VideoDecryptionEntity } from '@/domain/entities/video/videoDecryptionEntity';
import { cloudFunctionsService } from '@/data/services/cloudFunctionsService';
import { videoEncryptionService } from '@/domain/services/video/videoEncryptionService';
import { MediaUtils } from '@/domain/utils/mediaUtils';
import { VIDEO_SECRET_KEY_PREFIXES } from '@/domain/constants/videoConstants';

/**
 * Parameters for initializing a community video for encrypted playback.
 */
export interface CommunityVideoInitParams {
  /** Community message document id — keys the secrets/path; used as the encryption id. */
  messageId: string;

  /** Community-wall owner user id (access-gate counterparty for the CF gate + URL owner segment). */
  communityOwner: string;

  /** Current user firebase id (authentication + encryption). */
  uid: string;

  /** Firebase JWT issued-at timestamp. */
  iat: number;
}

export class InitializeCommunityVideoUseCase {
  constructor(
    private cloudFunctions = cloudFunctionsService,
    private encryptionService = videoEncryptionService,
  ) { }

  /**
   * Initialize a community video for encrypted playback.
   *
   * @param params - Community video initialization parameters.
   * @returns Result with the loader-compatible decryption entity + community
   *   Media CDN video URL on success; `{ success: false, error }` when the
   *   community callable returns null (no subscription / permission denied) or
   *   throws.
   */
  async execute(params: CommunityVideoInitParams): Promise<InitializeCommunityVideoResult> {
    const { messageId, communityOwner, uid, iat } = params;

    try {
      // 1. Get community-media CDN tokens + encrypted keys from the community
      //    callable. A null result is the no-subscription / permission-denied gate.
      const cdnTokens = await this.cloudFunctions.generateCommunityMediaCdnTokens(
        messageId,
        communityOwner,
      );

      if (!cdnTokens) {
        throw new Error('Failed to retrieve community media CDN tokens from cloud function');
      }

      // 2. Process encryption: decrypt + unshift keys. SAME service the chat /
      //    user-gen path uses; the community `messageId` is the encryption id.
      //    The COMMUNITY secret-name prefixes MUST be passed so the client
      //    unshifts with the same `expause-community-*` names the server shifted
      //    with — the user-gen default would produce a garbage key and an AES
      //    OperationError.
      const decryptionEntity = await this.encryptionService.processVideoEncryption(
        messageId,
        uid,
        iat,
        cdnTokens,
        VIDEO_SECRET_KEY_PREFIXES.community,
      );

      // 3. Build the community Media CDN video URL with the owner segment + hdnts.
      const videoUrl = MediaUtils.getCommunityVideoUrl(communityOwner, messageId, cdnTokens.hdnts);

      return {
        success: true,
        decryptionEntity,
        videoUrl,
        error: null,
      };
    } catch (error) {
      return {
        success: false,
        decryptionEntity: null,
        videoUrl: null,
        error: error instanceof Error ? error : new Error('Unknown error initializing community video'),
      };
    }
  }
}

/**
 * Result of community video initialization. Same shape as
 * {@link InitializeChatVideoResult} so the player wiring is loader-compatible.
 */
export interface InitializeCommunityVideoResult {
  /** Whether initialization succeeded. */
  success: boolean;

  /** Decryption entity (null if failed). */
  decryptionEntity: VideoDecryptionEntity | null;

  /** Full community Media CDN video URL with CDN token (null if failed). */
  videoUrl: string | null;

  /** Error if initialization failed. */
  error: Error | null;
}

// Export factory function for easy instantiation
export const createInitializeCommunityVideoUseCase = () => new InitializeCommunityVideoUseCase();
